import React, { useMemo } from "react";
import styled from "styled-components";
import { useGetTopicClusteringTreeCounts } from "@/utils/react-query-hooks/aiAnnotation";
import { useAggrMetricsAcrossTimeAndDimension } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetLineChart/query/useAggrMetricsAcrossTimeAndDimension";
import { parkedSubtopicId } from "@okf/ootils";
import { topicNodeFilter, signalsFilter, signalIdsInCategory } from "@/components/analysisModules/TopicClusteringTreeViewV2/scopeFilters";
import { UI_BODY_SEMIBOLD, UI_BODY_SEMIBOLD_SM, UI_BODY_SM, UI_CAPTION_DF, colors2, WrapperCardGrid, ButtonGhostCompact, SkeletonLoader } from "oolib";
import { useGetAnnotations, useGetAnnotationsLazy } from "@/utils/react-query-hooks/getData";
import { CategorySparklines, ObservationSparkline } from "./CategorySparklines";
import { LeadingCategoryTrend } from "./LeadingCategoryTrend";
import { useSignalCategories } from "./SignalCategoriesContext";
import { DatasetCircles } from "./DatasetCircles";
import { TopicQuickSummary } from "./TopicQuickSummary";
import { TopicCard, SignalChips, ClickableCard, CardTail, CardFooter, ObservationCount } from "./TopicCard";

/**
 * Layout 2 — grid of wide bucket cards (2-up at ~1000-1200px). Each card:
 * the bucket's label + description, and a single stacked bar of the bucket's
 * signal-CATEGORY distribution (per-anno counts aggregated by the BE).
 * Reuses the LensesV2 SentimentBar and the SignalsWidget category colours so
 * the palette means the same thing everywhere.
 *
 * Clicking a card drills into the bucket: its contents become a white header
 * band, and below sits one full-bleed section per signal category (biggest
 * share first, matching the bar) whose finding cards are the bucket's
 * encapsulations that lean that way — signal pill + encapsulation statement
 * + dataset provenance circles. Topic tags deliberately not shown at this
 * level.
 *
 * Observations no finding took ("unplaced" / "parked") get no cards of their
 * own: at every level they roll up into ONE muted finding-shaped card at the
 * end of the strip (or grid), which opens the same side panel a finding does,
 * listing the observations under it.
 */

// Category order, legend labels, drill-down titles and colours all come from
// the signal-category theme (useSignalCategories — the tenant's own list),
// which CategorySparklines reads too, so the drilldown bar and the
// sparklines under it can never disagree on order.

// The drilldown's header: a white band running edge to edge across the top
// of the content area — no border, no radius. It undoes the parent's
// padding (and the gap above the layout) with negative margins, then pads
// its own contents back in by the same amounts, so the text lines up with
// the widgets below while the white reaches the edges.
const HeaderBand = styled.div`
  background: ${colors2.white};
  margin: calc(-1 * (${({ $bleed }) => $bleed.y} + ${({ $bleed }) => $bleed.top}px))
    calc(-1 * ${({ $bleed }) => $bleed.x}) 0;
  padding: ${({ $bleed }) => $bleed.y} ${({ $bleed }) => $bleed.x} 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

// Back on the left; the sidebar's show-filters affordance on the right when
// the sidebar is collapsed (the parent's own toolbar row steps aside for
// this band).
const HeaderToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  /* Tighter to the title than the band's regular row gap. */
  margin-bottom: -8px;
`;

// The bar and the lines under it share one width, so the lines never run
// wider than the bar they explain.
const BarColumn = styled.div`
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// The category's mark, used in the findings table's Category column.
// Pinned to the bottom of the card so the line and the counts line up
// across cards of differing text length: the sparkline (when there is one)
// sits directly above the footer, both pushed down together.
// Same mark as the SentimentBar's legend dot, so the widget title and the
// bar's legend read as the same key.
const CategoryDot = styled.span`
  width: 8px;
  height: 12px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

// The grid's unplaced card: a cluster card on the muted ground.
const UnplacedClusterCard = styled(ClickableCard)`
  background: ${colors2.grey2};
`;

// `leadingFirst` reorders by share, biggest first (stable sort, so ties keep
// the canonical order). The cluster cards use it: the card's story is "what
// leads here", so that segment opens the bar and carries the only label.
const categorySegments = (signalCategoryCounts = {}, categoryTheme, { leadingFirst = false } = {}) => {
  const total = categoryTheme.list.reduce(
    (s, c) => s + (signalCategoryCounts[c.value] || 0),
    0
  );
  if (!total) return [];
  const segments = categoryTheme.list.map(({ value: category, display }) => ({
    key: category,
    category,
    title: display,
    value: Math.round(((signalCategoryCounts[category] || 0) / total) * 100),
    // Raw count, rendered in the legend between percent and label.
    count: signalCategoryCounts[category] || 0,
    color: categoryTheme.fillOf(category),
  }));
  return leadingFirst ? segments.sort((a, b) => b.count - a.count) : segments;
};

// Chip segments, one per signal (not per category) in the node's signalCounts,
// each coloured by its signal's category. Most-said first. signalCounts is the
// per-signal count map the counts payload already carries per node.
const signalSegments = (signalCounts = {}, signalMetaById = {}, categoryTheme) =>
  Object.entries(signalCounts || {})
    .filter(([, count]) => count > 0)
    .map(([signalId, count]) => {
      const meta = signalMetaById[signalId] || {};
      return {
        key: signalId,
        title: meta.display || signalId,
        count,
        value: count,
        color: meta.category ? categoryTheme.fillOf(meta.category) : colors2.grey40,
      };
    })
    .sort((a, b) => b.count - a.count);

// 3-level buckets drill into their encapsulations; legacy flat docs treat
// the bucket itself as its single row.
const rowsForBucket = (bucket) => (bucket.children?.length ? bucket.children : [bucket]);

// A set of parked observations as a ROW the side panel can open — the same
// shape a finding row has (label, description, observations, datasets,
// annotationCount) minus a signalId, since it's many signals' worth of
// evidence rather than one claim. `isUnplaced` lets the panel header say so.
const unplacedRowFor = ({ nodeId, category, observations }) => ({
  nodeId: `${nodeId}:unplaced:${category || "none"}`,
  isUnplaced: true,
  label: "Unplaced observations",
  // Same wording as the grid's unplaced card, one level down: findings are
  // the subtopics here.
  description: "Miscellaneous observations not fitting any sub-topic. Possible outliers.",
  observations,
  datasets: [...new Set(observations.map((o) => o.dataset).filter(Boolean))],
  annotationCount: observations.length,
});
// The same row from a COUNT (Topics V2 has no observations, only the parked
// list's per-category counts). `parkedOf` + `category` let the panel scope
// its annotation list to this topic's parked pseudo sub-topic and category.
const unplacedRowFromCount = ({ nodeId, category, count, datasets }) => ({
  nodeId: `${nodeId}:unplaced:${category || "none"}`,
  isUnplaced: true,
  parkedOf: String(nodeId),
  category: category || null,
  label: "Unplaced observations",
  description: "Miscellaneous observations not fitting any sub-topic. Possible outliers.",
  observations: [],
  datasets: datasets || [],
  annotationCount: count,
});

// `seriesScope` (Topics V2): { activeFilters, topicClusteringLens, period,
// dateFieldMap } — the scope the drilldown's lines are counted over. With
// it, the drilldown requests its own two time series (per signal for the
// header's category lines, per sub-topic for the cards' sparklines),
// answered from Elasticsearch, only while it is on screen. Without it (V1),
// the lines are drawn from the observation arrays the rows carry. While
// loading, a faint skeleton holds each line's place.
const LineSkeleton = ({ height }) => <SkeletonLoader style={{ height, width: "100%", opacity: 0.45, borderRadius: 4 }} />;

// ─── The findings table ─────────────────────────────────────────────────────
// The drilldown's findings were a per-category grid of cards; on the designer's
// ask they're one flat, sortable table instead. The category still rides along
// as a column (and tints the signal chip, the observations bar and the trend
// line), so nothing the cards said is lost. Clicking a row expands it in place
// to its description and an "Open N observations" entry into the side panel —
// the same panel the cards opened. Unplaced observations sit, muted, at the
// bottom.
const TablePanel = styled.div`
  margin-top: 24px;
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
  border-radius: 12px;
  overflow: hidden;
`;
const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;
const FTable = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
`;
const FTh = styled.th`
  text-align: ${({ $num }) => ($num ? "right" : "left")};
  padding: 11px 16px;
  white-space: nowrap;
  cursor: ${({ $sortable }) => ($sortable ? "pointer" : "default")};
  user-select: none;
  border-bottom: 1px solid ${colors2.grey10};
`;
const HeadLabel = styled(UI_CAPTION_DF)`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  white-space: nowrap;
  color: ${colors2.grey60};
`;
const FTr = styled.tr`
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? colors2.grey5 : "transparent")};
  transition: background 0.12s ease;
  &:hover {
    background: ${({ $selected }) => ($selected ? colors2.grey5 : colors2.grey2)};
  }
`;
const FTd = styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  text-align: ${({ $num }) => ($num ? "right" : "left")};
  border-bottom: 1px solid ${colors2.grey5};
`;
const Caret = styled.span`
  display: inline-block;
  width: 10px;
  flex-shrink: 0;
  font-size: 1.6rem;
  line-height: 1;
  color: ${colors2.grey40};
  transform: rotate(${({ $open }) => ($open ? "90deg" : "0deg")});
  transition: transform 0.15s ease;
`;
const CatCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ObsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;
const ObsTrack = styled.div`
  width: 72px;
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${colors2.grey10};
`;
const ObsFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  width: ${({ $pct }) => $pct}%;
`;
const TrendBox = styled.div`
  width: 120px;
  height: 30px;
`;
const ExpandBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 2px 0 6px 18px;
`;
// The observations listed under an expanded row, as a nested table:
// Observation (title + gist), What they said (the quote), Speaker, Date. The
// row carries the observations' ids and each observation's dateThatMatters;
// we fetch the full annotation docs (the quote, gist and speaker live there)
// and join the two by id. A short preview with a "See all" into the side
// panel, which still holds the full, filterable list.
const OBS_PREVIEW = 5;

const ObsPanel = styled.div`
  margin: 8px 0 4px 18px;
  background: ${colors2.white};
  border: 1px solid ${colors2.grey10};
  border-radius: 10px;
  overflow: hidden;
`;
const ObsTableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;
const ObsTable = styled.table`
  width: 100%;
  min-width: 1120px;
  border-collapse: collapse;
`;
const ObsTh = styled.th`
  text-align: ${({ $num }) => ($num ? "right" : "left")};
  padding: 9px 22px;
  white-space: nowrap;
  border-bottom: 1px solid ${colors2.grey10};
`;
const ObsTr = styled.tr`
  &:not(:last-child) td {
    border-bottom: 1px solid ${colors2.grey5};
  }
`;
const ObsTd = styled.td`
  padding: 11px 22px;
  vertical-align: top;
  text-align: ${({ $num }) => ($num ? "right" : "left")};
`;
const Quote = styled(UI_BODY_SM)`
  font-style: italic;
  color: ${colors2.grey80};
`;
const ObsFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid ${colors2.grey5};
  background: ${colors2.grey2};
`;
const SeeAll = styled(UI_BODY_SM)`
  color: ${colors2.primary};
  cursor: pointer;
  white-space: nowrap;
`;

// Lexical editorState → plain text: concat every text node under the root.
// The quote is stored as an editorState object (sometimes a JSON string).
const lexicalToText = (editorState) => {
  try {
    const root =
      typeof editorState === "string" ? JSON.parse(editorState).root : editorState?.root;
    const walk = (node) =>
      typeof node?.text === "string"
        ? node.text
        : Array.isArray(node?.children)
        ? node.children.map(walk).join("")
        : "";
    return walk(root).replace(/\s+/g, " ").trim();
  } catch {
    return "";
  }
};

// "28 Oct".
const fmtObsDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

// The annotation's "date that matters": the field dateFieldMap names for this
// doc's content type, read off the doc. (V1 rows carry the date on the stub;
// V2 rows don't, so we resolve it from the doc here.)
const resolveDocDate = (anno, dateFieldMap) => {
  const ct = anno?.meta?.contentType;
  const valuePath = ct && dateFieldMap?.[ct]?.valuePath;
  if (!valuePath) return null;
  return valuePath.split(".").reduce((o, k) => (o == null ? o : o[k]), anno);
};

// The observations under an expanded row. Two ways in: V1 rows carry the
// observations' ids (fetch by _id, date off the stub); V2 rows carry none
// (their observation arrays are emptied), so we load them by the row's node
// filter within the drilldown's scope — exactly as the side panel does — and
// read each date off the doc.
// The annotation's own signal: its non-persona tag (persona tagIds are
// prefixed personas_), resolved against signalMetaById by the tag's _id for
// the category colour.
const observationSignal = (anno, signalMetaById) => {
  const tagsData = Object.values(anno?.annotations?.tags || {}).find((d) => d?.collectionId === "tags")?.data || [];
  const tag = tagsData.find((t) => t?.tagId && !String(t.tagId).startsWith("personas_"));
  if (!tag) return null;
  const meta = signalMetaById[String(tag._id)];
  return { display: tag.display, category: meta?.category || null };
};

const ObservationsTable = ({ row, scope, signalMetaById, categoryTheme, onSeeAll }) => {
  const stubs = row.observations || [];
  const stubById = new Map(stubs.map((o) => [String(o._id), o]));
  const ids = stubs.map((o) => String(o._id)).filter(Boolean);
  const hasStubs = ids.length > 0;

  // V1 path: by id.
  const byIdQuery = useGetAnnotations({
    findQuery: { _id: { $in: ids } },
    queryOptions: { enabled: hasStubs },
  });

  // V2 path: by the row's node filter within the scope (mirrors SubtopicRowPanel).
  const panelFilters = useMemo(() => {
    if (hasStubs || !scope) return [];
    const base = scope.activeFilters || [];
    if (row.parkedOf) {
      return [
        ...base,
        topicNodeFilter(parkedSubtopicId(row.parkedOf), scope.topicClusteringLens),
        ...(row.category ? [signalsFilter(signalIdsInCategory(signalMetaById, row.category))].filter(Boolean) : []),
      ];
    }
    return [...base, topicNodeFilter(String(row.nodeId), scope.topicClusteringLens)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStubs, scope, row, signalMetaById]);
  const lazyQuery = useGetAnnotationsLazy({
    activeFilters: panelFilters,
    queryId: `findingsTable:${row.nodeId}`,
    queryOptions: { enabled: !hasStubs && !!scope },
  });

  const loading = hasStubs ? byIdQuery.status === "loading" : lazyQuery.isLoading;
  const annos = hasStubs
    ? byIdQuery.data?.data || []
    : (lazyQuery.data?.pages || []).flatMap((pg) => pg.data || []);
  const dateFieldMap = scope?.dateFieldMap;

  // Each visible observation: the fetched doc + its date, in a stable order.
  const items = hasStubs
    ? stubs
        .map((o) => ({ a: annos.find((x) => String(x._id) === String(o._id)), date: o.dateThatMatters }))
        .filter(({ a }) => a)
    : annos.map((a) => ({ a, date: resolveDocDate(a, dateFieldMap) }));

  if (hasStubs ? ids.length === 0 : !scope) return null;
  if (loading)
    return (
      <ObsPanel style={{ padding: 16 }}>
        <LineSkeleton height={96} />
      </ObsPanel>
    );
  const shown = items.slice(0, OBS_PREVIEW);
  if (shown.length === 0) return null;
  const total = hasStubs ? stubs.length : row.annotationCount ?? items.length;
  return (
    <ObsPanel>
      <ObsTableScroll>
        <ObsTable>
          <thead>
            <tr>
              <ObsTh><HeadLabel>Observation</HeadLabel></ObsTh>
              <ObsTh><HeadLabel>Explanation</HeadLabel></ObsTh>
              <ObsTh><HeadLabel>Signal</HeadLabel></ObsTh>
              <ObsTh><HeadLabel>What they said</HeadLabel></ObsTh>
              <ObsTh><HeadLabel>Speaker</HeadLabel></ObsTh>
              <ObsTh $num><HeadLabel>Date</HeadLabel></ObsTh>
            </tr>
          </thead>
          <tbody>
            {shown.map(({ a, date }) => {
              const stub = stubById.get(String(a._id));
              const title = a.annotations?.observation?.title || stub?.title || "Observation";
              const gist = a.annotations?.observation?.desc;
              const quote = lexicalToText(a.annotations?.fragment?.editorState);
              const speaker = a.annotations?.actorName;
              const signal = observationSignal(a, signalMetaById);
              return (
                <ObsTr key={String(a._id)}>
                  <ObsTd style={{ width: 480, maxWidth: 480 }}>
                    <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>{title}</UI_BODY_SEMIBOLD_SM>
                  </ObsTd>
                  <ObsTd style={{ width: 480, maxWidth: 480 }}>
                    {gist ? (
                      <UI_BODY_SM style={{ color: colors2.grey60 }}>{gist}</UI_BODY_SM>
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </ObsTd>
                  <ObsTd>
                    {signal?.display ? (
                      <SignalChips
                        segments={[{
                          key: String(a._id),
                          title: signal.display,
                          color: signal.category ? categoryTheme.fillOf(signal.category) : colors2.grey40,
                          value: 1,
                        }]}
                      />
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </ObsTd>
                  <ObsTd>
                    {quote ? (
                      <Quote>“{quote}”</Quote>
                    ) : (
                      <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                    )}
                  </ObsTd>
                  <ObsTd>
                    <UI_BODY_SM style={{ color: colors2.grey80, whiteSpace: "nowrap" }}>
                      {speaker || "—"}
                    </UI_BODY_SM>
                  </ObsTd>
                  <ObsTd $num>
                    <UI_BODY_SM style={{ color: colors2.grey60, whiteSpace: "nowrap" }}>
                      {fmtObsDate(date)}
                    </UI_BODY_SM>
                  </ObsTd>
                </ObsTr>
              );
            })}
          </tbody>
        </ObsTable>
      </ObsTableScroll>
      <ObsFooter>
        <UI_CAPTION_DF style={{ color: colors2.grey60 }}>
          Showing {shown.length} of {total}
        </UI_CAPTION_DF>
        {total > shown.length && (
          <SeeAll
            onClick={(e) => {
              e.stopPropagation();
              onSeeAll();
            }}
          >
            See all {total} observations →
          </SeeAll>
        )}
      </ObsFooter>
    </ObsPanel>
  );
};

const FINDING_COLS = [
  { key: "label", label: "Finding", sortable: true },
  { key: "signal", label: "Signal", sortable: true },
  { key: "category", label: "Category", sortable: true },
  { key: "observations", label: "Observations", sortable: true, num: true },
  { key: "trend", label: "Trend" },
  { key: "src", label: "Src", num: true },
];

const FindingsTable = ({
  realRows,
  unplacedRows,
  signalMetaById,
  categoryTheme,
  titleByContentType,
  series,
  period,
  scope,
  onRowClick,
  openRowNodeId,
}) => {
  const [sort, setSort] = React.useState({ key: "observations", dir: "desc" });
  const [expandedId, setExpandedId] = React.useState(null);

  const catTitle = (category) =>
    categoryTheme.list.find((c) => c.value === category)?.display || "";

  const sorted = React.useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...realRows].sort((a, b) => {
      switch (sort.key) {
        case "observations":
          return dir * ((a.row.annotationCount || 0) - (b.row.annotationCount || 0));
        case "signal":
          return dir * (a.signalMeta?.display || "").localeCompare(b.signalMeta?.display || "");
        case "category":
          return dir * catTitle(a.category).localeCompare(catTitle(b.category));
        default:
          return dir * (a.row.label || "").localeCompare(b.row.label || "");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realRows, sort, categoryTheme]);

  // Real findings sort; the muted unplaced rows always trail them.
  const rows = [...sorted, ...unplacedRows];
  if (rows.length === 0) return null;
  const maxObs = Math.max(1, ...realRows.map((r) => r.row.annotationCount || 0));

  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "observations" ? "desc" : "asc" }
    );

  return (
    <TablePanel>
      <TableScroll>
        <FTable>
          <thead>
            <tr>
              {FINDING_COLS.map((c) => (
                <FTh
                  key={c.key}
                  $num={c.num}
                  $sortable={c.sortable}
                  onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                >
                  <HeadLabel>
                    {c.label}
                    {sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
                  </HeadLabel>
                </FTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, signalMeta, category }) => {
              const mutedRow = !!row.isUnplaced;
              const nodeId = String(row.nodeId);
              const expanded = expandedId === nodeId;
              const color = category ? categoryTheme.fillOf(category) : colors2.grey40;
              const obs = row.annotationCount || 0;
              return (
                <React.Fragment key={nodeId}>
                  <FTr
                    $selected={nodeId === openRowNodeId}
                    onClick={() => setExpandedId((id) => (id === nodeId ? null : nodeId))}
                  >
                    <FTd>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Caret $open={expanded}>›</Caret>
                        <UI_BODY_SEMIBOLD_SM style={{ color: mutedRow ? colors2.grey60 : colors2.grey80 }}>
                          {row.label}
                        </UI_BODY_SEMIBOLD_SM>
                      </div>
                    </FTd>
                    <FTd>
                      {signalMeta ? (
                        <SignalChips
                          segments={[{
                            key: nodeId,
                            title: signalMeta.display,
                            color: categoryTheme.fillOf(signalMeta.category),
                            value: 1,
                          }]}
                        />
                      ) : (
                        <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                      )}
                    </FTd>
                    <FTd>
                      {category ? (
                        <CatCell>
                          <CategoryDot $color={color} />
                          <UI_BODY_SM style={{ color: colors2.grey80, whiteSpace: "nowrap" }}>
                            {catTitle(category)}
                          </UI_BODY_SM>
                        </CatCell>
                      ) : (
                        <UI_BODY_SM style={{ color: colors2.grey40 }}>—</UI_BODY_SM>
                      )}
                    </FTd>
                    <FTd $num>
                      <ObsCell>
                        <ObsTrack>
                          <ObsFill $pct={Math.round((obs / maxObs) * 100)} $color={color} />
                        </ObsTrack>
                        <UI_BODY_SM style={{ color: colors2.grey80, fontVariantNumeric: "tabular-nums" }}>
                          {obs}
                        </UI_BODY_SM>
                      </ObsCell>
                    </FTd>
                    <FTd>
                      {signalMeta && series?.loading ? (
                        <LineSkeleton height={28} />
                      ) : signalMeta ? (
                        <TrendBox>
                          <ObservationSparkline
                            observations={row.observations}
                            countsByBin={series?.bySubtopic?.[nodeId]}
                            period={period}
                            color={categoryTheme.accentOf(signalMeta.category)}
                            label={signalMeta.display}
                            opacity={0.25}
                          />
                        </TrendBox>
                      ) : null}
                    </FTd>
                    <FTd $num>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <DatasetCircles datasets={row.datasets} titleByContentType={titleByContentType} />
                      </div>
                    </FTd>
                  </FTr>
                  {expanded &&
                    (() => {
                      // Inline the observations whenever we can load them: the
                      // row carries their ids (V1), or the drilldown gave us a
                      // scope to load them by node filter (V2). Otherwise fall
                      // back to the button into the side panel.
                      const canInline = (row.observations || []).length > 0 || !!scope;
                      return (
                        <tr>
                          <FTd colSpan={FINDING_COLS.length} style={{ background: colors2.grey2 }}>
                            <ExpandBody>
                              {row.description && (
                                <UI_BODY_SM style={{ color: colors2.grey60, maxWidth: "60ch" }}>
                                  {row.description}
                                </UI_BODY_SM>
                              )}
                              {!canInline && (
                                <ButtonGhostCompact
                                  S
                                  icon="ArrowRight"
                                  value={`Open ${obs} observation${obs === 1 ? "" : "s"}`}
                                  stopPropagation
                                  onClick={() => onRowClick(row)}
                                />
                              )}
                            </ExpandBody>
                            {canInline && (
                              <ObservationsTable
                                row={row}
                                scope={scope}
                                signalMetaById={signalMetaById}
                                categoryTheme={categoryTheme}
                                onSeeAll={() => onRowClick(row)}
                              />
                            )}
                          </FTd>
                        </tr>
                      );
                    })()}
                </React.Fragment>
              );
            })}
          </tbody>
        </FTable>
      </TableScroll>
    </TablePanel>
  );
};
const BucketDrilldown = ({ bucket, signalMetaById, titleByContentType, onBack = null, onRowClick, openRowNodeId, period, prevWindow, prevSignalCategoryCounts, prevLoading = false, headerBleed, onShowFilters = null, seriesScope = null }) => {
  const categoryTheme = useSignalCategories();
  const series = useDrilldownSeries({ bucket, signalMetaById, seriesScope });
  // Group the bucket's encapsulations by their majority-signal's category,
  // then sort each card's rows by signal (display name, then statement) so
  // same-signal rows sit together instead of dispersing through the card.
  const rowsByCategory = new Map();
  rowsForBucket(bucket).forEach((row) => {
    const category = row.signalId ? signalMetaById[row.signalId]?.category : null;
    if (!category) return;
    if (!rowsByCategory.has(category)) rowsByCategory.set(category, []);
    rowsByCategory.get(category).push(row);
  });
  for (const rows of rowsByCategory.values()) {
    rows.sort((a, b) => {
      const byObservations = (b.annotationCount || 0) - (a.annotationCount || 0);
      if (byObservations) return byObservations;
      const sigA = signalMetaById[a.signalId]?.display || "";
      const sigB = signalMetaById[b.signalId]?.display || "";
      return sigA.localeCompare(sigB) || (a.label || "").localeCompare(b.label || "");
    });
  }

  // Parked observations carry a signal too, so they belong INSIDE their
  // category's widget rather than in a strip of their own. Anything whose
  // signal has no category (or no signal at all) can't be placed that way and
  // falls through to the tail section below the widgets.
  const parkedByCategory = new Map();
  const uncategorisedParked = [];
  (bucket.parkedObservations || []).forEach((o) => {
    const category = o.signalId ? signalMetaById[o.signalId]?.category : null;
    if (!category) {
      uncategorisedParked.push(o);
      return;
    }
    if (!parkedByCategory.has(category)) parkedByCategory.set(category, []);
    parkedByCategory.get(category).push(o);
  });
  for (const list of parkedByCategory.values()) {
    list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  }
  // Topics V2: no parked observations, but the parked list's counts per
  // category — one unplaced card per category from those, and the remainder
  // (parked under a signal with no category) as the tail card.
  const parkedCounts = bucket.parked?.signalCategoryCounts;
  const parkedFromCounts = !!parkedCounts && !(bucket.parkedObservations || []).length;
  const parkedCountByCategory = new Map();
  let uncategorisedParkedCount = 0;
  if (parkedFromCounts) {
    let categorised = 0;
    for (const [category, n] of Object.entries(parkedCounts)) {
      if (n > 0) parkedCountByCategory.set(category, n);
      categorised += n;
    }
    uncategorisedParkedCount = Math.max(0, (bucket.parked?.annotationCount || 0) - categorised);
  }

  // Same leading-first emphasis as the grid cards, carried through to the
  // lines: the bar, its one label, its trend chip and the highlighted line
  // all speak about the same category.
  const segments = categorySegments(bucket.signalCategoryCounts, categoryTheme, { leadingFirst: true });
  const leading = segments[0];

  // Flat rows for the findings table: every child finding (the card grid
  // grouped these by category; the table keeps them flat with a Category
  // column), then the category's and the tree's unplaced observations as
  // muted rows that always trail the real findings.
  const realRows = rowsForBucket(bucket).map((row) => {
    const signalMeta = row.signalId ? signalMetaById[row.signalId] : null;
    return { row, signalMeta, category: signalMeta?.category || null };
  });
  const unplacedRows = [];
  parkedByCategory.forEach((obs, category) =>
    unplacedRows.push({ row: unplacedRowFor({ nodeId: bucket.nodeId, category, observations: obs }), signalMeta: null, category })
  );
  parkedCountByCategory.forEach((count, category) =>
    unplacedRows.push({ row: unplacedRowFromCount({ nodeId: bucket.nodeId, category, count, datasets: bucket.datasets }), signalMeta: null, category })
  );
  if (uncategorisedParked.length)
    unplacedRows.push({ row: unplacedRowFor({ nodeId: bucket.nodeId, category: null, observations: uncategorisedParked }), signalMeta: null, category: null });
  if (uncategorisedParkedCount)
    unplacedRows.push({ row: unplacedRowFromCount({ nodeId: bucket.nodeId, category: null, count: uncategorisedParkedCount, datasets: bucket.datasets }), signalMeta: null, category: null });

  return (
    <div>
      {/* The clicked card's contents as the page header. */}
      <HeaderBand $bleed={headerBleed}>
        {/* No toolbar at all when the host has neither control to offer
            (a sidebar: its own close button stands in for Back). */}
        {(onBack || onShowFilters) && (
          <HeaderToolbar>
            {/* Same compact ghost as the filter sidebar's Collapse button. */}
            {onBack ? <ButtonGhostCompact S icon="CaretLeft" value="Back" onClick={onBack} /> : <span />}
            {onShowFilters && (
              <ButtonGhostCompact S icon="ArrowLineRight" value="Show Views & Filters" onClick={onShowFilters} />
            )}
          </HeaderToolbar>
        )}
        <div>
          <UI_BODY_SEMIBOLD>{bucket.label}</UI_BODY_SEMIBOLD>
          {bucket.description && (
            <UI_BODY_SM style={{ color: colors2.grey60 }}>{bucket.description}</UI_BODY_SM>
          )}
        </div>
        <BarColumn>
          {/* Same soft signal chips as the topic cards. */}
          <SignalChips segments={signalSegments(bucket.signalCounts, signalMetaById, categoryTheme)} />
          {series?.loading ? (
            <LineSkeleton height={80} />
          ) : (
            <CategorySparklines
              bucket={bucket}
              signalMetaById={signalMetaById}
              period={period}
              emphasisCategory={leading?.category}
              countsByCategory={series?.byCategory || null}
            />
          )}
        </BarColumn>
        <ObservationCount count={bucket.annotationCount} />
      </HeaderBand>

      {/* The quick summary: two or three lines on the topic, from the same
          scope. Only where the host hands over the comparison key (the
          Topics page, the dashboard's topic panel); a report widget's
          drilldown carries none and shows nothing. */}
      {seriesScope?.quickSummary && <TopicQuickSummary nodeType="topic" nodeId={String(bucket.nodeId)} scope={seriesScope} />}

      {/* Findings as one flat, sortable table — most-observed first by
          default. The category rides along as a column (and tints the signal
          chip, the observations bar and the trend line) instead of splitting
          the list into per-category sections; unplaced observations trail as
          muted rows. */}
      <FindingsTable
        realRows={realRows}
        unplacedRows={unplacedRows}
        signalMetaById={signalMetaById}
        categoryTheme={categoryTheme}
        titleByContentType={titleByContentType}
        series={series}
        period={period}
        scope={seriesScope}
        onRowClick={onRowClick}
        openRowNodeId={openRowNodeId}
      />
    </div>
  );
};

// The drilldown's two lines, requested by the drilldown itself (see the
// `seriesScope` note above). Both are counted on the annotations collection,
// bucketed on each dataset's "date that matters" (the same map the period
// filter uses), over the period's own interval, within the scope plus a
// Topics filter for this topic. The signal series folds into category lines
// for the header; the sub-topic series feeds each card's sparkline.
const useDrilldownSeries = ({ bucket, signalMetaById, seriesScope }) => {
  const { activeFilters = [], topicClusteringLens, period, dateFieldMap } = seriesScope || {};
  const enabled = !!seriesScope && !!bucket && !!period?.interval && !!dateFieldMap;
  const drillFilters = useMemo(
    () => (enabled ? [...activeFilters, topicNodeFilter(String(bucket.nodeId), topicClusteringLens)] : []),
    [enabled, activeFilters, bucket, topicClusteringLens]
  );
  const common = {
    contentType: "annotations",
    dateValuePathByContentType: dateFieldMap,
    timelineRange: period ? { from: period.from, to: period.to } : undefined,
    interval: period?.interval,
    activeFilters: drillFilters,
    queryOptions: { enabled },
  };
  const { data: signalRows, isLoading: signalLoading } = useAggrMetricsAcrossTimeAndDimension({
    ...common,
    vizCardId: `topicsV2:signals:${bucket?.nodeId}`,
    dimensionValuePath: "annotations.tags.tags",
  });
  const { data: subtopicRows, isLoading: subtopicLoading } = useAggrMetricsAcrossTimeAndDimension({
    ...common,
    vizCardId: `topicsV2:subtopics:${bucket?.nodeId}`,
    dimensionValuePath: enabled ? { dimensionType: "topicClusteringNode", level: "subtopic", parentTopicNodeId: String(bucket.nodeId) } : null,
  });
  return useMemo(() => {
    if (!seriesScope) return null;
    const byCategory = {};
    for (const row of signalRows || []) {
      for (const d of row.dimensions || []) {
        const category = signalMetaById[String(d.value)]?.category;
        if (!category || !d.count) continue;
        (byCategory[category] ||= {})[row.timeInterval] = (byCategory[category][row.timeInterval] || 0) + d.count;
      }
    }
    const bySubtopic = {};
    for (const row of subtopicRows || []) {
      for (const d of row.dimensions || []) {
        (bySubtopic[String(d.value)] ||= {})[row.timeInterval] = d.count;
      }
    }
    return { byCategory, bySubtopic, loading: signalLoading || subtopicLoading };
  }, [seriesScope, signalRows, subtopicRows, signalLoading, subtopicLoading, signalMetaById]);
};

// selectedBucket state lives in the parent so layout-independent chrome can
// hide while drilled in. `unplacedRow` is the tree-level leftovers no bucket
// took, already shaped as a row so the grid's card opens it like a finding.
// `onAskAI(bucket)`: opens the Ask AI chat anchored to this topic (split
// tenants). Optional — layouts without a chat entry pass nothing.
//
// The "vs last period" chip on every card and on the drilldown header reads
// the previous window's per-category counts. The host says which window:
//   prevScope   { topicClusteringLens, activeFilters } — the previous
//               window's scope; the layout requests the counts itself, sent
//               only once the cards have painted.
// `drilldownScope` is the drilldown's line scope (see BucketDrilldown).
// `drilldownBackButton`: false for a host whose own close button stands in
// for Back (the drilldown in a side panel). `gridMinWidth`: the topic
// cards' minimum width (a narrow host passes a smaller one).
export const CardGridLayout = ({ tree, signalMetaById, titleByContentType, selectedBucket, onSelectBucket, onRowClick, openRowNodeId, period, prevWindow, prevScope = null, headerBleed = { x: "0px", y: "0px", top: 0 }, onShowFilters = null, unplacedRow = null, drilldownScope = null, onAskAI = null, drilldownBackButton = true, gridMinWidth = "46rem" }) => {
  const categoryTheme = useSignalCategories();
  const { data: prevCounts } = useGetTopicClusteringTreeCounts({
    topicClusteringLens: prevScope?.topicClusteringLens,
    activeFilters: prevScope?.activeFilters || [],
    enabled: !!prevScope && !!prevWindow && !!tree,
  });
  const prevCountsByNodeId = useMemo(() => {
    if (!prevScope || !prevWindow || !prevCounts) return null;
    return Object.fromEntries((prevCounts.tree || []).map((topic) => [String(topic.nodeId), topic.signalCategoryCounts || {}]));
  }, [prevScope, prevWindow, prevCounts]);
  const prevPending = !!prevScope && !!prevWindow && !prevCountsByNodeId;
  if (selectedBucket) {
    return (
      <BucketDrilldown
        bucket={selectedBucket}
        signalMetaById={signalMetaById}
        titleByContentType={titleByContentType}
        onBack={drilldownBackButton ? () => onSelectBucket(null) : null}
        onRowClick={onRowClick}
        openRowNodeId={openRowNodeId}
        period={period}
        prevWindow={prevWindow}
        prevSignalCategoryCounts={prevCountsByNodeId?.[String(selectedBucket.nodeId)]}
        prevLoading={prevPending}
        headerBleed={headerBleed}
        onShowFilters={onShowFilters}
        seriesScope={drilldownScope}
      />
    );
  }

  // Buckets with the most observations first.
  const buckets = [...(tree.tree || [])].sort(
    (a, b) => (b.annotationCount || 0) - (a.annotationCount || 0)
  );

  return (
    <WrapperCardGrid minWidth={gridMinWidth}>
      {buckets.map((bucket) => {
        // One chip per signal the topic carries (from the counts payload's
        // per-signal counts), most-said first, each coloured by its category.
        const segments = signalSegments(bucket.signalCounts, signalMetaById, categoryTheme);
        return (
          <TopicCard
            key={String(bucket.nodeId)}
            title={bucket.label}
            description={bucket.description}
            segments={segments}
            count={bucket.annotationCount}
            datasets={bucket.datasets}
            titleByContentType={titleByContentType}
            // The Ask AI entry sits beside the dataset circles and must not
            // also open the drilldown.
            footerRight={
              onAskAI && (
                <ButtonGhostCompact
                  S
                  icon="Sparkle"
                  value="Ask AI"
                  // oolib debounces onClick by 200ms, so stopping
                  // propagation inside the handler is too late — the card's
                  // drilldown would open first. This prop is applied
                  // synchronously in the button's own wrapper.
                  stopPropagation
                  onClick={() => onAskAI(bucket)}
                />
              )
            }
            onClick={() => onSelectBucket(bucket)}
          />
        );
      })}
      {/* Last in the grid: the observations no topic would take, as one
          muted cluster card that opens them in the finding panel. */}
      {unplacedRow && (
        <UnplacedClusterCard onClick={() => onRowClick(unplacedRow)}>
          <div>
            <UI_BODY_SEMIBOLD style={{ color: colors2.grey60 }}>{unplacedRow.label}</UI_BODY_SEMIBOLD>
            <UI_BODY_SM style={{ color: colors2.grey60 }}>{unplacedRow.description}</UI_BODY_SM>
          </div>
          <CardTail>
            <CardFooter>
              <ObservationCount count={unplacedRow.annotationCount} />
              <DatasetCircles datasets={unplacedRow.datasets} titleByContentType={titleByContentType} />
            </CardFooter>
          </CardTail>
        </UnplacedClusterCard>
      )}
    </WrapperCardGrid>
  );
};
